import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Divider,
  Button,
} from '@mui/material';
import { CalendarToday, ExpandMore, EventBusy, CheckCircle, FileDownload, PictureAsPdf } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';

interface ItemPendente { letra: string; nome: string; }
interface PendenciaEixo { eixo: string; eixo_nome: string | null; itens: ItemPendente[]; }
interface PacienteAgenda {
  nome: string;
  cns: string | null;
  cpf: string | null;
  telefone: string | null;
  hora: string | null;
  em_acompanhamento: boolean;
  populacoes: (string | null)[];
  pendencias: PendenciaEixo[];
  total_pendencias: number;
}
interface ProfissionalAgenda {
  co_lotacao: number | null;
  profissional: string;
  cbo: string | null;
  cbo_nome: string | null;
  unidade: string | null;
  pacientes: PacienteAgenda[];
}
interface AgendaResponse {
  data: string;
  total_agendados: number;
  total_em_acompanhamento: number;
  profissionais: ProfissionalAgenda[];
  dwh_implementado?: boolean;
}

/** Data de hoje em yyyy-mm-dd (horario local, sem shift de UTC). */
function hojeISO(): string {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

const SaudeAgendaDoDia: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [data, setData] = useState<string>(hojeISO());
  const [somenteAcompanhamento, setSomenteAcompanhamento] = useState<boolean>(true);
  const [profInput, setProfInput] = useState<string>('');
  const [resultado, setResultado] = useState<AgendaResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);

  // Valor efetivo do filtro por profissional: aplica so com >=3 caracteres (senao, todos).
  const profAplicado = profInput.trim().length >= 3 ? profInput.trim() : '';

  const fetchAgenda = useCallback(async () => {
    if (!municipioAtivo || !unidadeSelecionada || !data) return;
    setLoading(true);
    setErro(null);
    try {
      const resp: AgendaResponse = await saudeService.getAgendaPendencias(
        municipioAtivo.id,
        data,
        unidadeSelecionada.codunidade,
        unidadeSelecionada.codcnes,
        profAplicado || undefined,
      );
      setResultado(resp);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Erro ao carregar a agenda do dia.');
      setResultado(null);
    } finally {
      setLoading(false);
    }
  }, [municipioAtivo, unidadeSelecionada, data, profAplicado]);

  useEffect(() => {
    fetchAgenda();
  }, [fetchAgenda]);

  // Filtro de exibicao (client-side, sem nova consulta):
  // ON  -> somente pacientes em algum acompanhamento/indicador (C2-C7);
  // OFF -> todos os agendados da unidade.
  const profissionais = useMemo(() => {
    if (!resultado) return [];
    if (!somenteAcompanhamento) return resultado.profissionais;
    return resultado.profissionais
      .map((p) => ({ ...p, pacientes: p.pacientes.filter((pac) => pac.em_acompanhamento) }))
      .filter((p) => p.pacientes.length > 0);
  }, [resultado, somenteAcompanhamento]);

  const exportarCsv = useCallback(() => {
    if (!municipioAtivo || !unidadeSelecionada) return;
    saudeService.exportarAgendaCsv(
      municipioAtivo.id, data, unidadeSelecionada.codunidade, unidadeSelecionada.codcnes,
      profAplicado || undefined, somenteAcompanhamento,
    ).catch(() => setErro('Falha ao exportar CSV.'));
  }, [municipioAtivo, unidadeSelecionada, data, profAplicado, somenteAcompanhamento]);

  const exportarPdf = useCallback(() => {
    if (!municipioAtivo || !unidadeSelecionada) return;
    saudeService.exportarAgendaPdf(
      municipioAtivo.id, data, unidadeSelecionada.codunidade, unidadeSelecionada.codcnes,
      profAplicado || undefined, somenteAcompanhamento,
    ).catch(() => setErro('Falha ao exportar PDF.'));
  }, [municipioAtivo, unidadeSelecionada, data, profAplicado, somenteAcompanhamento]);

  const totalExibido = useMemo(
    () => profissionais.reduce((s, p) => s + p.pacientes.length, 0),
    [profissionais],
  );

  if (!municipioAtivo) {
    return <Alert severity="info" sx={{ m: 3 }}>Selecione um municipio.</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabecalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <CalendarToday color="primary" />
        <Typography variant="h5" fontWeight={700}>Agenda do dia</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pacientes agendados na unidade e as boas praticas pendentes de cada um, por profissional.
      </Typography>

      {!unidadeSelecionada ? (
        <Alert severity="warning">
          Selecione uma <strong>unidade</strong> no seletor da barra lateral para ver a agenda do dia.
        </Alert>
      ) : (
        <>
          {/* Filtros */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Dia"
              type="date"
              size="small"
              value={data}
              onChange={(e) => setData(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            <TextField
              label="Profissional"
              size="small"
              placeholder="Filtra ao digitar 3 letras"
              value={profInput}
              onChange={(e) => setProfInput(e.target.value)}
              sx={{ width: 240, '& .MuiInputBase-input::placeholder': { fontSize: '0.75rem' } }}
            />
            <Chip
              icon={<CalendarToday sx={{ fontSize: 16 }} />}
              label={unidadeSelecionada.nome}
              variant="outlined"
            />
            <FormControlLabel
              sx={{ ml: 'auto' }}
              control={<Switch checked={somenteAcompanhamento} onChange={(e) => setSomenteAcompanhamento(e.target.checked)} />}
              label="Somente em acompanhamento"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<FileDownload />} size="small" onClick={exportarCsv} disabled={loading}>
                CSV
              </Button>
              <Button variant="outlined" startIcon={<PictureAsPdf />} size="small" color="error" onClick={exportarPdf} disabled={loading}>
                PDF
              </Button>
            </Box>
          </Paper>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          )}

          {!loading && erro && <Alert severity="error">{erro}</Alert>}

          {!loading && !erro && resultado?.dwh_implementado === false && (
            <Alert severity="info">
              A agenda do dia nao esta disponivel para este municipio (conexao via Data Warehouse).
            </Alert>
          )}

          {!loading && !erro && resultado && resultado.dwh_implementado !== false && resultado.total_agendados > 0 && (
            <>
              {/* Resumo */}
              <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>{resultado.total_agendados}</strong> agendados na unidade
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{resultado.total_em_acompanhamento}</strong> em acompanhamento (C2-C7)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{totalExibido}</strong> exibidos
                </Typography>
              </Box>

              {profissionais.length === 0 ? (
                <Alert severity="info" icon={<CheckCircle />}>
                  Nenhum dos pacientes agendados esta em acompanhamento (C2-C7). Desative
                  &nbsp;<strong>Somente em acompanhamento</strong>&nbsp; para ver todos os agendados.
                </Alert>
              ) : (
                profissionais.map((prof) => {
                  const pendProf = prof.pacientes.reduce((s, p) => s + p.total_pendencias, 0);
                  return (
                    <Accordion key={String(prof.co_lotacao ?? prof.profissional)} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography fontWeight={600}>{prof.profissional || 'Profissional'}</Typography>
                          {prof.cbo_nome && (
                            <Typography variant="caption" color="text.secondary">{prof.cbo_nome}</Typography>
                          )}
                          <Chip size="small" label={`${prof.pacientes.length} paciente(s)`} />
                          {pendProf > 0 && (
                            <Chip size="small" color="warning" label={`${pendProf} pendencia(s)`} />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: 70 }}>Hora</TableCell>
                                <TableCell>Paciente</TableCell>
                                <TableCell>Acompanhamento</TableCell>
                                <TableCell>Pendencias</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {prof.pacientes.map((pac, idx) => (
                                <TableRow key={pac.cns || pac.cpf || `${prof.co_lotacao}-${idx}`} hover>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{pac.hora || '--'}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={500}>{pac.nome}</Typography>
                                    {pac.telefone && (
                                      <Typography variant="caption" color="text.secondary">{pac.telefone}</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {pac.populacoes.filter(Boolean).length === 0 ? (
                                      <Typography variant="caption" color="text.disabled">—</Typography>
                                    ) : (
                                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {pac.populacoes.filter(Boolean).map((pop, i) => (
                                          <Chip key={i} size="small" variant="outlined" label={pop} />
                                        ))}
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {!pac.em_acompanhamento ? (
                                      <Typography variant="caption" color="text.disabled">—</Typography>
                                    ) : pac.total_pendencias === 0 ? (
                                      <Chip size="small" color="success" variant="outlined" icon={<CheckCircle />} label="Em dia" />
                                    ) : (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        {pac.pendencias.map((pd) => (
                                          <Box key={pd.eixo}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              {pd.eixo_nome}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                              {pd.itens.map((it) => (
                                                <Chip key={it.letra} size="small" color="warning" variant="outlined" label={it.nome} />
                                              ))}
                                            </Box>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </>
          )}

          {!loading && !erro && resultado && resultado.dwh_implementado !== false && resultado.total_agendados === 0 && (
            <Alert severity="info" icon={<EventBusy />} sx={{ mt: 2 }}>
              Nenhum paciente agendado nesta unidade em {data.split('-').reverse().join('/')}.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default SaudeAgendaDoDia;
